import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  GenericIconName,
  Icon,
  Text,
  useOnClickOutside,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {useRef} from 'react';
import {useRect} from 'react-viewport-utils';
import sc, {css} from 'styled-components';

interface OnboardingHintProps {
  yourFilterButtonRef: React.RefObject<HTMLDivElement | null>;
  onClose: VoidFunction;
}

export const OnboardingHint = ({
  yourFilterButtonRef,
  onClose,
  ref: forwardedRef,
}: OnboardingHintProps & {ref?: React.Ref<HTMLDivElement>}) => {
  const {t} = useTranslation();

  const innerRef = useRef(null);
  const ref = forwardedRef || innerRef;

  useOnClickOutside(ref as React.RefObject<HTMLDivElement | null>, onClose, {
    trigger: 'once',
  });

  const yourFilterButtonRect = useRect(yourFilterButtonRef, {
    disableScrollUpdates: true,
    priority: 'low',
  });

  if (!yourFilterButtonRect) {
    return null;
  }

  const arrowPosition = yourFilterButtonRect.width / 2;

  return (
    <Wrapper
      $arrowPosition={arrowPosition}
      data-testid="yourFiltersHint"
      ref={ref}
    >
      <StyledIcon asset={GenericIconName.YourFiltersHint} size="XL" />
      <div>
        <Text weight="bold" size="px16">
          {t('Global Filter sets')}
        </Text>
        <Badge>{t('NEW')}</Badge>
        <Description as="p" size="px12">
          {t(
            'Tired of always applying the same filters for the same search? Just apply any filter you like and click on the "Save Filter" Button to save the filter combination. Your personal filter combinations will be available in this dropdown and can be re-accessed from anywhere on the page.',
          )}
        </Description>
      </div>
      <CloseIcon
        data-testid="yourFiltersHintCloseIcon"
        asset={GenericIconName.Close}
        size="XS"
        fill={COLORS.Grey1}
        onClick={onClose}
      />
    </Wrapper>
  );
};

const arrowStyles = css`
  content: '';
  display: block;
  position: absolute;
  bottom: 100%;
  width: 0;
  height: 0;
`;

const ICON_SIZE = 8;

const Wrapper = sc.div<{$arrowPosition: number}>`
  display: flex;
  align-items: center;

  position: absolute;
  width: max-content;
  max-width: 500px;
  background-color: ${COLORS.White};
  border: 1px solid ${COLORS.Grey2};
  border-radius: 6px;
  padding: 12px 24px 12px 16px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.2);

  &:before {
    ${arrowStyles}
    left: ${({$arrowPosition}) => `${$arrowPosition - ICON_SIZE}px`};
    border: ${ICON_SIZE}px solid transparent;
    border-bottom-color: ${COLORS.Grey2};
  }

  &:after {
    ${arrowStyles}
    left: ${({$arrowPosition}) => `${$arrowPosition - ICON_SIZE + 1}px`};
    border: 7px solid transparent;
    border-bottom-color: white;
  }
`;

const Badge = linariaStyled.div`
  display: inline-block;
  width: fit-content;
  font-size: ${FONT_SIZES.px10};
  background-color: ${COLORS.Green};
  border-radius: 3px;
  padding: 2px 4px;
  margin-left: 6px;
`;

const Description = linariaStyled(Text)`
  margin-top: 6px;
  line-height: 1.3;
  white-space: break-spaces;
`;

const StyledIcon = linariaStyled(Icon)`
  margin-right: 10px;
`;

const CloseIcon = linariaStyled(Icon)`
  position: absolute;
  top: 6px;
  right: 6px;
  cursor: pointer;
`;
